import React, { useState, useRef, useEffect } from 'react';
import ReactApexChart from "react-apexcharts";
import { useTranslation } from "react-i18next";
import moment from 'moment';
import { useTheme } from '../ThemeContext.js';
import { Dropdown, Image, Card, Accordion } from "react-bootstrap";
import { motion, AnimatePresence } from 'framer-motion';

//import api
import { getHedgingList } from '../../api/tradeAction.js';

//import lib
import { toFixed } from '../../lib/roundOf.js';

export default function PortfolioPerformance(props) {

  const { t, i18n } = useTranslation()
  const { theme, toggleTheme } = useTheme();


  //props
  const { piecurrency } = props;


  //state
  const [chartdata, setchartdata] = useState([]);
  const [chartDate, setchartDate] = useState([]);
  const [filterType, setFilterType] = useState('year'); // Initial filter type
  const [pairId, setpairId] = useState(''); // Initial filter type
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const chartOptions = {
    chart: {
      foreColor: theme === "dark" ? "#fff" : "#666",
      toolbar: {
        show: false,
      },
      type: "bar",
      id: "bar-chart",
      zoom: {
        enabled: false,
      },
      dropShadow: {
        enabled: false,
      },
      sparkline: {
        enabled: false,
      },
    },
    dataLabels: {
      enabled: false,
    },
    stroke: {
      show: false,
      width: 0,
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: "30%",
        borderRadius: 1,
        dataLabels: {
          position: "top",
        },
        colors: {
          ranges: [
            {
              from: 0,
              to: Infinity,
              color: theme === "dark" ? "#395645" : "#395645"
            }
          ]
        }
      },
    },
    tooltip: {
      theme: theme,
      y: {
        formatter: function (value) {
          return toFixed(value, 4);
        },
      },
      style: {
        fontSize: "12px",
      }
    },
    grid: {
      borderColor: theme === "dark" ? "#404040" : "#e5e7eb",
      row: {
        colors: ["transparent", "transparent"],
        opacity: 0,
      },
      column: {
        colors: ["transparent", "transparent"],
        opacity: 0,
      },
      // xaxis: {
      //   lines: {
      //     show: true,
      //   },
      // },
      yaxis: {
        lines: {
          show: true,
        },
      },
      padding: {
        left: 0,
        right: 0,
      },
    },
    xaxis: {
      type: "category",
      categories: chartDate,
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
      labels: {
        style: {
          fontSize: "12px",
          fontWeight: 400,
          colors: theme === "dark" ? "#999" : "#999",
        },
      },
    },
    yaxis: {
      axisBorder: {
        show: true,
      },
      axisTicks: {
        show: false,
      },
      tickAmount: 5,
      labels: {
        formatter: function (value) {
          if (value >= 1000000000000) {
            return (value / 1000000000000).toFixed(0) + 'T';
          } else if (value >= 1000000000) {
            return (value / 1000000000).toFixed(0) + 'B';
          } else if (value >= 1000000) {
            return (value / 1000000).toFixed(0) + 'M';
          }
          return value.toString();
        },
        style: {
          fontSize: "12px",
          fontWeight: 400,
          colors: theme === "dark" ? "#999" : "#999",
        },
      },
    },
    colors: [theme === "dark" ? "#93c5fd" : "#c7d2fe"],
    states: {
      hover: {
        filter: {
          type: "none",
          value: 0,
        },
      },
      active: {
        filter: {
          type: "none",
          value: 0,
        },
      },
    },
  };

  const fetchData = async () => {
    try {
      let data = {
        filter: filterType,
        pairId: pairId?._id,
      };
      const { status, result } = await getHedgingList(data);
      if (status === "success") {
        let seriesData = {};
        let dateArray = [];

        result.forEach(item => {
          const currency = item.firstCurrency;
          // const colorObj = piecurrency && piecurrency.length > 0 && piecurrency.find((el) => el.currency === currency);
          // const color = colorObj ? colorObj.colorCode : '#36A2EB';

          if (!seriesData[currency]) {
            seriesData[currency] = {
              name: currency,
              data: [],
              // color: color,
              date: []
            };
          }
          seriesData[currency].data.push(item.total);
          let date
          if (filterType === 'year') {
            date = moment().month(item.month - 1).format('MMM');
          } else if (filterType === 'month' || filterType === 'week') {
            date = moment(item.date).format("MMM D");
          } else {
            const change = new Date(item.date);
            const options = { month: 'short', day: '2-digit', hour: '2-digit', hour12: false, timeZone: 'UTC' };
            date = new Intl.DateTimeFormat('en-US', options).format(change);

            // date = moment(item.date).format('MMM DD H');
          }
          dateArray = [...new Set([...dateArray, date])];
        });
        const dataArray = Object.values(seriesData);
        setchartdata(dataArray);
        setchartDate(dateArray);
      }
    } catch (err) {
      console.log(err, "error");
    }
  };


  const handleFilterChange = (filter) => {
    setFilterType(filter);
  };

  const handlePairChange = (pair) => {
    setpairId(pair);
  }

  useEffect(() => {
    fetchData();
  }, [filterType, pairId]);


  useEffect(() => {
    if (piecurrency.length > 0) {
      setpairId(piecurrency[0])
    }
  }, [piecurrency])

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="">
      <div className="flex flex-col sm:flex-row items-stretch gap-4">
        <div className="rounded-sm outline outline-1 outline-offset-[-1px] outline-gray-500/25 flex justify-between sm:justify-start items-stretch w-full sm:w-auto">
          <button onClick={() => handleFilterChange('day')} className={`flex-1 sm:flex-none px-3 py-1.5 ${filterType === 'day' ? 'bg-[#395645]' : 'bg-transparent border-r border-gray-500/25'} rounded-tl-sm rounded-bl-sm inline-flex flex-col justify-start items-center gap-6 border-0`}>
            <div className={`justify-center ${filterType === 'day' ? 'text-white' : 'text-gray-500 hover:text-[#395645]'} text-sm font-medium font-['Roobert'] transition-colors`}>{t("DAY")}</div>
          </button>
          <button onClick={() => handleFilterChange('week')} className={`flex-1 sm:flex-none px-3 py-1.5 ${filterType === 'week' ? 'bg-[#395645]' : 'bg-transparent border-r border-gray-500/25'} inline-flex flex-col justify-center items-center gap-4 border-0`}>
            <div className={`justify-center ${filterType === 'week' ? 'text-white' : 'text-gray-500 hover:text-[#395645]'} text-sm font-medium font-['Roobert'] transition-colors`}>{t("WEEK")}</div>
          </button>
          <button onClick={() => handleFilterChange('month')} className={`flex-1 sm:flex-none px-3 py-1.5 ${filterType === 'month' ? 'bg-[#395645]' : 'bg-transparent border-r border-gray-500/25'} inline-flex flex-col justify-center items-center gap-4 border-0`}>
            <div className={`justify-center ${filterType === 'month' ? 'text-white' : 'text-gray-500 hover:text-[#395645]'} text-sm font-medium font-['Roobert'] transition-colors`}>{t("MONTH")}</div>
          </button>
          <button onClick={() => handleFilterChange('year')} className={`flex-1 sm:flex-none px-3 py-1.5 ${filterType === 'year' ? 'bg-[#395645]' : 'bg-transparent'} rounded-tr-sm rounded-br-sm inline-flex flex-col justify-center items-center gap-4 border-0`}>
            <div className={`justify-center ${filterType === 'year' ? 'text-white' : 'text-gray-500 hover:text-[#395645]'} text-sm font-medium font-['Roobert'] transition-colors`}>{t("YEAR")}</div>
          </button>
        </div>
        
        <div className="flex items-stretch w-full sm:w-auto">
          <div className="relative inline-flex items-stretch z-10 w-full sm:w-auto" ref={dropdownRef}>
            <div 
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className="w-full h-full px-2 py-1.5 rounded-sm outline outline-1 outline-offset-[-1px] outline-gray-500/25 flex justify-between items-center gap-4 cursor-pointer bg-white"
            >
              <div className="flex justify-start items-center gap-2">
                <img className="w-5 h-5 rounded-full object-cover" src={pairId?.image || "https://placehold.co/20x20"} alt={pairId?.currency} />
                <div className="justify-center text-neutral-700 text-sm font-normal font-['Roobert']">{pairId?.currency || "Select"}</div>
              </div>
              <div className={`w-3.5 h-3.5 relative overflow-hidden transition-transform duration-200 flex justify-center items-center ${isDropdownOpen ? 'rotate-180' : ''}`}>
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path fillRule="evenodd" clipRule="evenodd" d="M3.09581 5.08258C3.16353 4.91913 3.32306 4.8125 3.50001 4.8125L10.5 4.8125C10.677 4.8125 10.8365 4.91907 10.9042 5.08258C10.9719 5.24603 10.9345 5.43422 10.8094 5.55934L7.30946 9.05936C7.1386 9.23021 6.86157 9.23022 6.69072 9.05936L3.19066 5.5594C3.06553 5.43427 3.0281 5.24609 3.09581 5.08258Z" fill="#6B7280" />
                </svg>
              </div>
            </div>
            <AnimatePresence>
              {isDropdownOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -10, height: 0 }}
                  animate={{ opacity: 1, y: 0, height: "auto" }}
                  exit={{ opacity: 0, y: -10, height: 0 }}
                  transition={{ duration: 0.2 }}
                  className="absolute top-full left-0 mt-1 w-full bg-white rounded-sm shadow-lg outline outline-1 outline-gray-500/25 overflow-hidden flex flex-col min-w-max"
                >
                  {piecurrency.map((option, i) => (
                    <div
                      key={i}
                      onClick={() => {
                        handlePairChange(option);
                        setIsDropdownOpen(false);
                      }}
                      className="flex items-center gap-2 px-3 py-2 hover:bg-gray-100 cursor-pointer transition-colors"
                    >
                      <img className="w-5 h-5 rounded-full object-cover" src={option.image} alt={option.currency} />
                      <span className="text-sm font-normal font-['Roobert'] text-neutral-700">{option.currency}</span>
                    </div>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      <ReactApexChart
        options={chartOptions}
        series={chartdata.map((series) => ({
          name: series.name,
          data: series.data
        }))}
        type="bar"
        height={500}
      />
    </div>
  );
}
